import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { Table } from '@components/Common';
import { formatLocalDateTime } from '@helpers/dateUtils';
import { useAdminFilters } from '@hooks/useAdminFilters';
import AdminFiltersControlled, { filterItemsByDateTime } from './AdminFiltersControlled';
import AdminNavigation from './AdminNavigation';

// TanStack Query hooks
import { useLoadingInfosQuery, LoadingInfo } from '@api/queries/loadingInfos';

const LoadingInfosList: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const { filters, dateFilter, setDateFilter } = useAdminFilters();

  // TanStack Query for data fetching
  const { data: items = [], isLoading, refetch } = useLoadingInfosQuery(filters.startDate);

  const filteredItems = useMemo(() => filterItemsByDateTime(items, x => x.receivedAt, dateFilter), [items, dateFilter]);

  const columns: Column[] = [
    {
      Header: 'Id',
      id: 'id',
      accessor: 'id',
      show: false
    },
    {
      Header: t('admin.received_at'),
      id: 'receivedAt',
      accessor: 'receivedAt',
      Cell: ({ value }) => formatLocalDateTime(value, true),
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: t('admin.delivery_date'),
      id: 'deliveryDate',
      accessor: 'deliveryDate',
      Cell: ({ value }) => formatLocalDateTime(value, false),
      show: true,
      maxWidth: 170,
      filterable: false
    },
    {
      Header: t('entry.terminal'),
      id: 'TerminalName',
      accessor: 'terminalName',
      show: true,
      maxWidth: 150
    },
    {
      Header: t('entry.customer'),
      id: 'CustomerName',
      accessor: 'customerName',
      show: true,
      maxWidth: 350
    },
    {
      Header: t('admin.delivery_number'),
      id: 'DeliveryNumber',
      accessor: 'deliveryNumber',
      show: true,
      maxWidth: 150
    },
    {
      Header: t('admin.item_number'),
      id: 'ItemNumber',
      accessor: 'itemNumber',
      show: true,
      maxWidth: 150
    },
    {
      Header: t('admin.ship_to'),
      id: 'shipTo',
      accessor: (item: LoadingInfo) =>
        [item.shipToName1, item.shipToName2, item.shipToStreet, item.shipToPostalCode, item.shipToCity]
          .filter(x => x && x.trim().length > 0)
          .join(', '),
      show: true,
      maxWidth: 350
    },
    {
      Header: t('enabled'),
      id: 'IsEnabled',
      accessor: (item: LoadingInfo) => item.isEnabled,
      Cell: ({ value }) => (value ? t('filter_active') : t('filter_inactive')),
      show: true,
      maxWidth: 90,
      filterMethod: (filter, row) => (filter.value !== '' ? row[filter.id] === (filter.value === 'true') : true),
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : ''}
        >
          <option value="" />
          <option value="true">{t('filter_active')}</option>
          <option value="false">{t('filter_inactive')}</option>
        </select>
      )
    },
    {
      Header: 'XML',
      id: 'OriginalJsonData',
      accessor: 'originalJsonData',
      show: true
    }
  ];

  return (
    <>
      <AdminNavigation isEnabled={true} isLoading={isLoading} fetch={() => refetch()} />
      <AdminFiltersControlled value={dateFilter} onChange={setDateFilter} />
      <Table
        data={filteredItems}
        columns={columns}
        filterable={true}
        defaultSortColumn="receivedAt"
        defaultSortDesc={true}
        defaultPageSize={20}
        noDataText={t('entry.no_rows_bookkeeping_reporting')}
      />
    </>
  );
};

export default LoadingInfosList;
